import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { API_BASE_URL } from '../config/api';
import { showToast } from '../utils/toast';
import html2canvas from 'html2canvas';
import './Hrana.css';

const VARIATION_KEY_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const nextMealVariationKey = (existingKeys) => {
  const set = new Set(existingKeys.map((k) => String(k).toUpperCase()));
  for (let i = 0; i < VARIATION_KEY_LETTERS.length; i += 1) {
    const c = VARIATION_KEY_LETTERS[i];
    if (!set.has(c)) return c;
  }
  return `V${existingKeys.length + 1}`;
};

const sortMealVariationKeys = (keys) =>
  [...keys].sort((a, b) => {
    const sa = String(a).toUpperCase();
    const sb = String(b).toUpperCase();
    if (sa.length === 1 && sb.length === 1) return sa.localeCompare(sb);
    return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
  });

const createEmptyMealVariation = (mealNumber, variationKey) => ({
  mealNumber,
  variationKey,
  title: '',
  foods: [],
  foodGrams: []
});

const normalizeMealFromServer = (row, mealNumber, variationKey) => {
  const vk = (row.variation_key || variationKey || 'A').toString().toUpperCase();
  const titleRaw = row.meal_title != null ? String(row.meal_title).trim() : '';
  return {
    mealNumber: Number(row.meal_number) || mealNumber,
    variationKey: vk,
    title: titleRaw,
    foods: JSON.parse(row.foods || '[]'),
    foodGrams: JSON.parse(row.food_grams || '[]')
  };
};

const DOSE_UNITS = ['mg', 'g', 'IU'];

const parseDailySupplementsFromUser = (raw) => {
  if (!raw) return { supplements: [''], amounts: [''], times: [''], doses: [''] };
  try {
    const p = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const supplements = Array.isArray(p.supplements) ? p.supplements : [];
    const amounts = Array.isArray(p.supplementAmounts) ? p.supplementAmounts : [];
    const times = Array.isArray(p.supplementTimes) ? p.supplementTimes : [];
    const doses = Array.isArray(p.supplementDoses) ? p.supplementDoses : [];
    if (supplements.length === 0) return { supplements: [''], amounts: [''], times: [''], doses: [''] };
    while (amounts.length < supplements.length) amounts.push('');
    while (times.length < supplements.length) times.push('');
    while (doses.length < supplements.length) doses.push('');
    return { supplements, amounts, times, doses };
  } catch {
    return { supplements: [''], amounts: [''], times: [''], doses: [''] };
  }
};

const trimDailySupplementEdges = (supplements, amounts, times, doses = []) => {
  let last = supplements.length - 1;
  while (last >= 0 && !supplements[last]) last -= 1;
  const s = supplements.slice(0, Math.max(0, last + 1));
  const a = amounts.slice(0, Math.max(0, last + 1));
  const t = times.slice(0, Math.max(0, last + 1));
  const d = doses.slice(0, Math.max(0, last + 1));
  if (s.length === 0) return { supplements: [''], amounts: [''], times: [''], doses: [''] };
  return { supplements: s, amounts: a, times: t, doses: d };
};

const ensureArraySize = (array, index) => {
  const copy = [...array];
  while (copy.length <= index) {
    copy.push('');
  }
  return copy;
};

const removeAtIndex = (array, index) => {
  const copy = [...array];
  if (index < copy.length) {
    copy.splice(index, 1);
  }
  return copy;
};

const trimMealEdges = (meal) => {
  let lastFoodIndex = meal.foods.length - 1;
  while (lastFoodIndex >= 0 && !meal.foods[lastFoodIndex]) {
    lastFoodIndex -= 1;
  }
  meal.foods = meal.foods.slice(0, lastFoodIndex + 1);
  meal.foodGrams = meal.foodGrams.slice(0, lastFoodIndex + 1);

  return meal;
};

function Hrana() {
  const { user } = useAuth();
  const { t, foodDisplayName } = useLanguage();
  const [foods, setFoods] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [mealPlanVariations, setMealPlanVariations] = useState([]);
  const [dailySupplements, setDailySupplements] = useState({ supplements: [''], amounts: [''], times: [''], doses: [''] });
  const [selectedMealNumber, setSelectedMealNumber] = useState(1);
  const [mealCount, setMealCount] = useState(6);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Admin-specific state
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCustomerInfo, setSelectedCustomerInfo] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Template state
  const [mealTemplates, setMealTemplates] = useState([]);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateSelectedMeals, setTemplateSelectedMeals] = useState([]);
  // Load-template mapping modal
  const [loadTemplateModal, setLoadTemplateModal] = useState(null); // { template, mapping: [{tmplMealNumber, targetSlot}] }
  // Supplement templates
  const [supplementTemplates, setSupplementTemplates] = useState([]);
  const [showSuppTemplateModal, setShowSuppTemplateModal] = useState(false);
  const [suppTemplateName, setSuppTemplateName] = useState('');
  const [savingSuppTemplate, setSavingSuppTemplate] = useState(false);


  // Tips state
  const [mealTips, setMealTips] = useState([]);
  const [expandedTip, setExpandedTip] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'coach';
  const targetUserId = isAdmin && selectedCustomer ? selectedCustomer.id : user?.id;
  const mealTableRef = useRef(null);
  const mealExportRef = useRef(null);

  useEffect(() => {
    fetchFoods();
    fetchSupplements();
    
    if (isAdmin) {
      fetchCustomers();
      fetchMealTemplates();
      fetchSupplementTemplates();
    } else if (user) {
      setMealCount(user.meal_count || 6);
      initializeMealVariations(user.meal_count || 6);
      fetchMealPlans(user.id);
      fetchDailySupplementsForUser(user.id);
    }
  }, [user, isAdmin]);

  const fetchMealTemplates = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/templates/meals`);
      setMealTemplates(res.data);
    } catch {
      // silently ignore
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    try {
      const grouped = new Map();
      mealPlanVariations.forEach((mv) => {
        const mn = mv.mealNumber;
        if (templateSelectedMeals.length > 0 && !templateSelectedMeals.includes(mn)) return;
        if (!grouped.has(mn)) grouped.set(mn, { meal_number: mn, variations: [] });
        grouped.get(mn).variations.push({
          key: mv.variationKey,
          title: mv.title != null ? String(mv.title).trim() : '',
          foods: mv.foods.filter(Boolean),
          foodGrams: mv.foodGrams
        });
      });
      const data = Array.from(grouped.values()).sort((a, b) => a.meal_number - b.meal_number);
      await axios.post(`${API_BASE_URL}/api/templates/meals`, {
        name: templateName.trim(),
        meal_count: mealCount,
        data
      });
      showToast.success(t('templates.savedSuccess'));
      setShowSaveTemplateModal(false);
      setTemplateName('');
      setTemplateSelectedMeals([]);
      fetchMealTemplates();
    } catch {
      showToast.error('Error saving template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleLoadMealTemplate = (option) => {
    if (!option) return;
    const tmpl = option.template;
    const tmplMeals = (tmpl.data || []).map((m) => m.meal_number);
    // Default mapping: each template meal maps to same-numbered slot if it exists, else first slot
    const mapping = tmplMeals.map((mn) => ({
      tmplMealNumber: mn,
      targetSlot: mn <= mealCount ? mn : 1,
      include: true
    }));
    setLoadTemplateModal({ template: tmpl, mapping });
  };

  const handleApplyLoadTemplate = () => {
    if (!loadTemplateModal) return;
    const { template: tmpl, mapping } = loadTemplateModal;
    // Build a lookup: tmplMealNumber -> targetSlot (only included ones)
    const activeMap = {};
    mapping.forEach(({ tmplMealNumber, targetSlot, include }) => {
      if (include) activeMap[tmplMealNumber] = targetSlot;
    });
    // Build new variations: keep existing meals that aren't being replaced, then add template meals
    const replacedSlots = new Set(Object.values(activeMap));
    const kept = mealPlanVariations.filter((mv) => !replacedSlots.has(mv.mealNumber));
    const added = [];
    (tmpl.data || []).forEach((meal) => {
      const targetSlot = activeMap[meal.meal_number];
      if (targetSlot == null) return;
      (meal.variations || []).forEach((v) => {
        added.push({
          mealNumber: targetSlot,
          variationKey: v.key || 'A',
          title: v.title || '',
          foods: v.foods || [],
          foodGrams: v.foodGrams || []
        });
      });
    });
    setMealPlanVariations([...kept, ...added]);
    setLoadTemplateModal(null);
    showToast.success(t('templates.loadedSuccess'));
  };

  useEffect(() => {
    if (isAdmin && selectedCustomer && selectedCustomerInfo) {
      const customerMealCount = selectedCustomerInfo.meal_count || 6;
      setMealCount(customerMealCount);
      // Reset meals array first, then fetch
      setMealPlanVariations([]);
      fetchMealPlans(selectedCustomer.id);
      fetchDailySupplementsForUser(selectedCustomer.id);
    }
  }, [selectedCustomer, selectedCustomerInfo]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      const customerList = response.data.filter(u => u.role === 'customer');
      setCustomers(customerList);
      
      // Get full details for customer options
      const customerOptions = await Promise.all(
        customerList.map(async (customer) => {
          try {
            const userDetails = await axios.get(`${API_BASE_URL}/api/users/${customer.id}`);
            return { ...customer, meal_count: userDetails.data.meal_count || 6 };
          } catch {
            return { ...customer, meal_count: 6 };
          }
        })
      );
      
      setCustomers(customerOptions);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const parseTips = (raw) => {
    if (!raw) return [];
    try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return []; }
  };

  const handleCustomerChange = async (selectedOption) => {
    if (selectedOption) {
      setSelectedCustomer(selectedOption);
      try {
        const userDetails = await axios.get(`${API_BASE_URL}/api/users/${selectedOption.id}`);
        setSelectedCustomerInfo(userDetails.data);
        setMealCount(userDetails.data.meal_count || 6);
        setDailySupplements(parseDailySupplementsFromUser(userDetails.data.daily_supplements));
        setMealTips(parseTips(userDetails.data.meal_tips));
        // Reset meals to empty array to trigger proper reload
        setMealPlanVariations([]);
      } catch (error) {
        console.error('Error fetching customer details:', error);
      }
    } else {
      setSelectedCustomer(null);
      setSelectedCustomerInfo(null);
      setMealPlanVariations([]);
      setDailySupplements({ supplements: [''], amounts: [''], times: [''], doses: [''] });
      setMealTips([]);
      setMealCount(6);
    }
  };

  const handleAddTip = () => {
    setMealTips((prev) => [...prev, { id: Date.now().toString(), title: '', body: '' }]);
  };

  const handleTipChange = (id, field, value) => {
    setMealTips((prev) => prev.map((tip) => tip.id === id ? { ...tip, [field]: value } : tip));
  };

  const handleDeleteTip = (id) => {
    setMealTips((prev) => prev.filter((tip) => tip.id !== id));
    if (expandedTip === id) setExpandedTip(null);
  };

  const fetchDailySupplementsForUser = async (userId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/users/${userId}`);
      setDailySupplements(parseDailySupplementsFromUser(res.data.daily_supplements));
      if (!isAdmin) setMealTips(parseTips(res.data.meal_tips));
    } catch (e) {
      console.error('Error fetching daily supplements:', e);
      setDailySupplements({ supplements: [''], amounts: [''], times: [''], doses: [''] });
    }
  };

  const initializeMealVariations = (count) => {
    const merged = [];
    for (let m = 1; m <= count; m += 1) {
      merged.push(createEmptyMealVariation(m, 'A'));
    }
    setMealPlanVariations(merged);
    setLoading(false);
  };

  const fetchFoods = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/foods`);
      setFoods(response.data);
    } catch (error) {
      console.error('Error fetching foods:', error);
    }
  };

  const fetchSupplements = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/supplements`);
      setSupplements(response.data);
    } catch (error) {
      console.error('Error fetching supplements:', error);
    }
  };

  const fetchMealPlans = async (userId) => {
    try {
      // Get the correct meal count first
      let userMealCount = 6;
      
      if (isAdmin && selectedCustomerInfo) {
        userMealCount = selectedCustomerInfo.meal_count || 6;
      } else if (user) {
        userMealCount = user.meal_count || 6;
      }
      
      setMealCount(userMealCount);

      const merged = [];
      const response = await axios.get(`${API_BASE_URL}/api/meal-plans/${userId}`);

      const byKey = new Map();
      (response.data || []).forEach((row) => {
        const mealNumber = Number(row.meal_number) || 1;
        if (mealNumber < 1 || mealNumber > userMealCount) return;
        const variationKey = (row.variation_key || 'A').toString().toUpperCase();
        byKey.set(
          `${mealNumber}-${variationKey}`,
          normalizeMealFromServer(row, mealNumber, variationKey)
        );
      });

      for (let m = 1; m <= userMealCount; m += 1) {
        const keysForSlot = sortMealVariationKeys(
          [...byKey.keys()]
            .filter((k) => k.startsWith(`${m}-`))
            .map((k) => k.split('-')[1])
        );
        if (keysForSlot.length === 0) {
          merged.push(createEmptyMealVariation(m, 'A'));
        } else {
          keysForSlot.forEach((variationKey) => {
            const fullKey = `${m}-${variationKey}`;
            merged.push(
              byKey.get(fullKey) || createEmptyMealVariation(m, variationKey)
            );
          });
        }
      }

      setMealPlanVariations(merged);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching meal plans:', error);
      const userMealCount = isAdmin && selectedCustomerInfo 
        ? (selectedCustomerInfo.meal_count || 6)
        : (user?.meal_count || 6);
      initializeMealVariations(userMealCount);
    }
  };

  const handleFoodSelect = (variationIndex, rowIndex, option) => {
    setMealPlanVariations((prevMeals) => {
      const updated = [...prevMeals];
      const mealCopy = { ...updated[variationIndex] };
      mealCopy.foods = ensureArraySize(mealCopy.foods, rowIndex);
      mealCopy.foodGrams = ensureArraySize(mealCopy.foodGrams, rowIndex);
      mealCopy.foods[rowIndex] = option ? option.value : '';
      if (!option) {
        mealCopy.foodGrams[rowIndex] = '';
      }
      trimMealEdges(mealCopy);
      updated[variationIndex] = mealCopy;
      return updated;
    });
  };

  const handleFoodGramChange = (variationIndex, rowIndex, value) => {
    setMealPlanVariations((prevMeals) => {
      const updated = [...prevMeals];
      const mealCopy = { ...updated[variationIndex] };
      mealCopy.foodGrams = ensureArraySize(mealCopy.foodGrams, rowIndex);
      mealCopy.foodGrams[rowIndex] = value;
      trimMealEdges(mealCopy);
      updated[variationIndex] = mealCopy;
      return updated;
    });
  };

  const handleDailySupplementSelect = (rowIndex, option) => {
    setDailySupplements((prev) => {
      const supplements = ensureArraySize([...prev.supplements], rowIndex);
      const amounts = ensureArraySize([...prev.amounts], rowIndex);
      const times = ensureArraySize([...prev.times], rowIndex);
      const doses = ensureArraySize([...(prev.doses || [])], rowIndex);
      supplements[rowIndex] = option ? option.value : '';
      if (!option) {
        amounts[rowIndex] = '';
        times[rowIndex] = '';
        doses[rowIndex] = '';
      }
      return trimDailySupplementEdges(supplements, amounts, times, doses);
    });
  };

  const handleDailyAmountChange = (rowIndex, value) => {
    setDailySupplements((prev) => {
      const supplements = [...prev.supplements];
      const amounts = ensureArraySize([...prev.amounts], rowIndex);
      const times = ensureArraySize([...prev.times], rowIndex);
      const doses = ensureArraySize([...(prev.doses || [])], rowIndex);
      amounts[rowIndex] = value;
      return trimDailySupplementEdges(supplements, amounts, times, doses);
    });
  };

  const handleDailyTimeChange = (rowIndex, value) => {
    setDailySupplements((prev) => {
      const supplements = [...prev.supplements];
      const amounts = ensureArraySize([...prev.amounts], rowIndex);
      const times = ensureArraySize([...prev.times], rowIndex);
      const doses = ensureArraySize([...(prev.doses || [])], rowIndex);
      times[rowIndex] = value;
      return trimDailySupplementEdges(supplements, amounts, times, doses);
    });
  };

  const handleDailyDoseChange = (rowIndex, value) => {
    setDailySupplements((prev) => {
      const supplements = [...prev.supplements];
      const amounts = ensureArraySize([...prev.amounts], rowIndex);
      const times = ensureArraySize([...prev.times], rowIndex);
      const doses = ensureArraySize([...(prev.doses || [])], rowIndex);
      doses[rowIndex] = value;
      return trimDailySupplementEdges(supplements, amounts, times, doses);
    });
  };

  const handleAddDailySupplementRow = () => {
    setDailySupplements((prev) => ({
      supplements: [...prev.supplements, ''],
      amounts: [...prev.amounts, ''],
      times: [...prev.times, ''],
      doses: [...(prev.doses || []), '']
    }));
  };

  const fetchSupplementTemplates = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/templates/supplements`);
      setSupplementTemplates(res.data || []);
    } catch {
      // silently ignore
    }
  };

  const handleSaveSupplementTemplate = async () => {
    const name = suppTemplateName.trim();
    if (!name) return;
    const dd = trimDailySupplementEdges(
      dailySupplements.supplements,
      dailySupplements.amounts,
      dailySupplements.times,
      dailySupplements.doses || []
    );
    const rows = dd.supplements
      .map((s, i) => ({ s, a: dd.amounts[i] || '', t: dd.times[i] || '', d: (dd.doses || [])[i] || '' }))
      .filter((r) => r.s);
    if (rows.length === 0) {
      showToast.warning(t('hrana.suppTemplateEmpty'));
      return;
    }
    setSavingSuppTemplate(true);
    try {
      await axios.post(`${API_BASE_URL}/api/templates/supplements`, {
        name,
        data: {
          supplements: rows.map((r) => r.s),
          amounts: rows.map((r) => r.a),
          times: rows.map((r) => r.t),
          doses: rows.map((r) => r.d)
        }
      });
      showToast.success(t('templates.savedSuccess'));
      setShowSuppTemplateModal(false);
      setSuppTemplateName('');
      fetchSupplementTemplates();
    } catch {
      showToast.error('Error saving template');
    } finally {
      setSavingSuppTemplate(false);
    }
  };

  const handleLoadSupplementTemplate = (option) => {
    const data = option?.template?.data;
    if (!data || !Array.isArray(data.supplements) || data.supplements.length === 0) return;
    const hasExisting = dailySupplements.supplements.some(Boolean);
    if (hasExisting && !window.confirm(t('hrana.suppTemplateReplaceConfirm'))) return;
    const n = data.supplements.length;
    const pad = (arr) => {
      const out = Array.isArray(arr) ? arr.slice(0, n) : [];
      while (out.length < n) out.push('');
      return out;
    };
    setDailySupplements({
      supplements: [...data.supplements],
      amounts: pad(data.amounts),
      times: pad(data.times),
      doses: pad(data.doses)
    });
    showToast.success(t('templates.loadedSuccess'));
  };

  const handleRemoveDailySupplementRow = (rowIndex) => {
    setDailySupplements((prev) => {
      const supplements = removeAtIndex([...prev.supplements], rowIndex);
      const amounts = removeAtIndex([...prev.amounts], rowIndex);
      const times = removeAtIndex([...prev.times], rowIndex);
      const doses = removeAtIndex([...(prev.doses || [])], rowIndex);
      return trimDailySupplementEdges(supplements, amounts, times, doses);
    });
  };

  const handleAddMealVariation = () => {
    setMealPlanVariations((prev) => {
      const slot = selectedMealNumber;
      const keys = prev.filter((m) => m.mealNumber === slot).map((m) => m.variationKey);
      const nk = nextMealVariationKey(keys);
      let insertAt = prev.length;
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        if (prev[i].mealNumber === slot) {
          insertAt = i + 1;
          break;
        }
      }
      const next = [...prev];
      next.splice(insertAt, 0, createEmptyMealVariation(slot, nk));
      return next;
    });
  };

  const handleRemoveMealVariation = (variationIndex) => {
    setMealPlanVariations((prev) => {
      const target = prev[variationIndex];
      if (!target) return prev;
      const sameSlot = prev.filter((m) => m.mealNumber === target.mealNumber);
      if (sameSlot.length <= 1) return prev;
      return prev.filter((_, i) => i !== variationIndex);
    });
  };

  const handleMealTitleChange = (variationIndex, value) => {
    setMealPlanVariations((prev) => {
      const updated = [...prev];
      const mealCopy = { ...updated[variationIndex], title: value };
      updated[variationIndex] = mealCopy;
      return updated;
    });
  };

  const handleRemoveRow = (variationIndex, rowIndex) => {
    setMealPlanVariations((prevMeals) => {
      const updated = [...prevMeals];
      const mealCopy = { ...updated[variationIndex] };
      mealCopy.foods = removeAtIndex(mealCopy.foods, rowIndex);
      mealCopy.foodGrams = removeAtIndex(mealCopy.foodGrams, rowIndex);
      trimMealEdges(mealCopy);
      updated[variationIndex] = mealCopy;
      return updated;
    });
  };

  const handleSave = async () => {
    if (!isAdmin) {
      alert('Only admin or coach can save meal plans');
      return;
    }

    if (!selectedCustomer) {
      alert('Please select a customer first');
      return;
    }

    const grouped = new Map();
    mealPlanVariations.forEach((mv) => {
      const mn = mv.mealNumber;
      if (!grouped.has(mn)) {
        grouped.set(mn, { meal_number: mn, variations: [] });
      }
      const foods = [];
      const foodGrams = [];
      mv.foods.forEach((food, idx) => {
        if (food && food.trim()) {
          foods.push(food.trim());
          foodGrams.push(mv.foodGrams[idx] || '');
        }
      });
      grouped.get(mn).variations.push({
        key: mv.variationKey,
        title: mv.title != null && String(mv.title).trim() ? String(mv.title).trim() : null,
        foods,
        foodGrams,
        supplements: [],
        supplementAmounts: []
      });
    });

    const payload = Array.from(grouped.values()).sort((a, b) => a.meal_number - b.meal_number);

    const dd = trimDailySupplementEdges(
      dailySupplements.supplements,
      dailySupplements.amounts,
      dailySupplements.times,
      dailySupplements.doses || []
    );
    const dailySuppList = [];
    const dailyAmtList = [];
    const dailyTimeList = [];
    const dailyDoseList = [];
    dd.supplements.forEach((s, idx) => {
      if (s && String(s).trim()) {
        dailySuppList.push(String(s).trim());
        dailyAmtList.push(dd.amounts[idx] || '');
        dailyTimeList.push(dd.times[idx] || '');
        dailyDoseList.push(dd.doses[idx] || '');
      }
    });

    try {
      await axios.put(`${API_BASE_URL}/api/users/${selectedCustomer.id}/daily-supplements`, {
        supplements: dailySuppList,
        supplementAmounts: dailyAmtList,
        supplementTimes: dailyTimeList,
        supplementDoses: dailyDoseList
      });
      await axios.post(`${API_BASE_URL}/api/meal-plans/${selectedCustomer.id}`, { meals: payload });
      const cleanTips = mealTips.filter((tip) => tip.title.trim() || tip.body.trim());
      await axios.put(`${API_BASE_URL}/api/users/${selectedCustomer.id}/meal-tips`, { tips: cleanTips });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving meal plans:', error);
      alert('Error saving meal plans');
    }
  };

  const foodOptions = useMemo(
    () => foods.map((f) => ({ value: f.name, label: foodDisplayName(f.name, f.name_en) })),
    [foods, foodDisplayName]
  );
  const supplementOptions = supplements.map(s => ({ value: s.name, label: s.name }));
  const customerOptions = customers.map(c => ({ value: c, label: c.username, id: c.id }));

  const selectMenuProps = useMemo(() => {
    if (typeof document === 'undefined') {
      return { menuPortalTarget: null, menuPosition: undefined, styles: {} };
    }

    return {
      menuPortalTarget: document.body,
      menuPosition: 'fixed',
      styles: {
        menuPortal: (base) => ({ ...base, zIndex: 9999 })
      }
    };
  }, []);

  const mealSlotOptions = useMemo(
    () =>
      Array.from({ length: Math.max(0, mealCount || 0) }, (_, i) => ({
        value: i + 1,
        label: `${t('hrana.meal')} ${i + 1}`
      })),
    [mealCount, t]
  );

  const visibleMealVariations = useMemo(
    () => mealPlanVariations.filter((m) => m.mealNumber === selectedMealNumber),
    [mealPlanVariations, selectedMealNumber]
  );

  const targetPlanUserId = isAdmin && selectedCustomer ? selectedCustomer.id : user?.id;

  useEffect(() => {
    setSelectedMealNumber(1);
  }, [targetPlanUserId]);

  useEffect(() => {
    if (mealCount > 0 && selectedMealNumber > mealCount) {
      setSelectedMealNumber(mealCount);
    }
  }, [mealCount, selectedMealNumber]);

  const handleDownloadMealImage = async () => {
    const source = mealExportRef.current || mealTableRef.current;
    if (!source || isDownloading) return;

    try {
      setIsDownloading(true);
      const canvas = await html2canvas(source, {
        backgroundColor: '#ffffff',
        scale: window.devicePixelRatio || 2,
        scrollX: 0,
        scrollY: -window.scrollY
      });

      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = 'meal-plan.png';
      link.click();
    } catch (error) {
      console.error('Error generating meal plan image:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const getFoodMeta = (name) => foods.find((f) => f.name === name);

  const getFoodDisplayUnit = (food) => {
    if (!food) return 'grams';
    return food.serving_type === 'piece' ? 'pieces' : 'grams';
  };

  const convertInputToGrams = (food, value) => {
    const numeric = parseFloat(value) || 0;
    if (!food || numeric <= 0) return 0;

    const type = food.serving_type === 'piece' ? 'piece' : 'grams';
    if (type === 'grams') {
      return numeric;
    }

    const gramsPerServing =
      typeof food.grams_per_serving === 'number' && food.grams_per_serving > 0
        ? food.grams_per_serving
        : 50;
    return numeric * gramsPerServing;
  };

  // Calculate nutrition totals for a meal
  const calculateMealNutrition = (meal) => {
    let totalProtein = 0;
    let totalCarbs = 0;
    let totalFats = 0;
    let totalCalories = 0;

    meal.foods.forEach((foodName, index) => {
      const food = getFoodMeta(foodName);
      const rawValue = meal.foodGrams[index];
      const grams = convertInputToGrams(food, rawValue);
      
      if (food && grams > 0) {
        const multiplier = grams / 100; // Convert to per 100g ratio
        totalProtein += (food.protein_per_100g || 0) * multiplier;
        totalCarbs += (food.carbs_per_100g || 0) * multiplier;
        totalFats += (food.fats_per_100g || 0) * multiplier;
        totalCalories += (food.calories_per_100g || 0) * multiplier;
      }
    });

    return {
      protein: Math.round(totalProtein * 10) / 10, // Round to 1 decimal
      carbs: Math.round(totalCarbs * 10) / 10,
      fats: Math.round(totalFats * 10) / 10,
      calories: Math.round(totalCalories * 10) / 10
    };
  };

  if (loading && !isAdmin) {
    return (
      <div className="container">
        <div className="card">
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hrana-container">
      <div className="container">
        <div className="card">
          <div className="hrana-header">
            <h2>{t('hrana.title')}</h2>
            <div className="header-actions">
              {isAdmin && selectedCustomer && mealTemplates.length > 0 && (
                <Select
                  options={mealTemplates.map(tmpl => ({ value: tmpl.id, label: tmpl.name, template: tmpl }))}
                  placeholder={t('templates.selectTemplate')}
                  onChange={handleLoadMealTemplate}
                  value={null}
                  isClearable={false}
                  classNamePrefix="react-select"
                  className="template-select"
                />
              )}
              {isAdmin && selectedCustomer && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowSaveTemplateModal(true)}
                >
                  {t('templates.saveAsTemplate')}
                </button>
              )}
              {isAdmin && selectedCustomer && (
                <button onClick={handleSave} className="btn-primary">
                  {t('common.saveChanges')}
                </button>
              )}
              {saved && <span className="saved-indicator">{t('common.saved')}</span>}
            </div>
          </div>

          {showSaveTemplateModal && (
            <div className="save-template-modal-overlay" onClick={() => { setShowSaveTemplateModal(false); setTemplateSelectedMeals([]); }}>
              <div className="save-template-modal" onClick={(e) => e.stopPropagation()}>
                <h3>{t('templates.saveAsTemplate')}</h3>
                <label>{t('templates.templateName')}</label>
                <input
                  type="text"
                  className="field-input"
                  placeholder={t('templates.templateNamePlaceholder')}
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveAsTemplate()}
                />
                <div className="template-meal-picker">
                  <label className="template-meal-picker-label">{t('templates.selectMeals')}</label>
                  <label className="template-meal-checkbox">
                    <input
                      type="checkbox"
                      checked={templateSelectedMeals.length === 0}
                      onChange={() => setTemplateSelectedMeals([])}
                    />
                    {t('templates.allMeals')}
                  </label>
                  {Array.from({ length: mealCount }, (_, i) => i + 1).map((mn) => (
                    <label key={mn} className="template-meal-checkbox">
                      <input
                        type="checkbox"
                        checked={templateSelectedMeals.includes(mn)}
                        onChange={() => setTemplateSelectedMeals((prev) =>
                          prev.includes(mn) ? prev.filter((x) => x !== mn) : [...prev, mn]
                        )}
                      />
                      {t('hrana.meal')} {mn}
                    </label>
                  ))}
                </div>
                <div className="save-template-modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => { setShowSaveTemplateModal(false); setTemplateSelectedMeals([]); }}>
                    {t('templates.cancel')}
                  </button>
                  <button type="button" className="btn-primary" onClick={handleSaveAsTemplate} disabled={savingTemplate || !templateName.trim()}>
                    {t('templates.saveTemplate')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {loadTemplateModal && (
            <div className="save-template-modal-overlay" onClick={() => setLoadTemplateModal(null)}>
              <div className="save-template-modal load-template-modal" onClick={(e) => e.stopPropagation()}>
                <h3>{t('templates.loadTemplate')}</h3>
                <p className="load-template-subtitle">
                  Izaberi koji obroci iz šablona da zamene postojeće obroke klijenta.
                </p>
                <div className="load-template-mapping">
                  <div className="load-template-mapping-header">
                    <span>Obrok iz šablona</span>
                    <span>Uključi</span>
                    <span>Zameni obrok №</span>
                  </div>
                  {loadTemplateModal.mapping.map((row, idx) => (
                    <div key={row.tmplMealNumber} className="load-template-mapping-row">
                      <span className="load-template-meal-label">
                        {(() => {
                          const tmplMeal = (loadTemplateModal.template.data || []).find((m) => m.meal_number === row.tmplMealNumber);
                          const firstTitle = tmplMeal?.variations?.[0]?.title;
                          return firstTitle ? `${t('hrana.meal')} ${row.tmplMealNumber} — ${firstTitle}` : `${t('hrana.meal')} ${row.tmplMealNumber}`;
                        })()}
                      </span>
                      <input
                        type="checkbox"
                        className="load-template-include"
                        checked={row.include}
                        onChange={() => setLoadTemplateModal((prev) => ({
                          ...prev,
                          mapping: prev.mapping.map((r, i) => i === idx ? { ...r, include: !r.include } : r)
                        }))}
                      />
                      <select
                        className="load-template-slot-select"
                        value={row.targetSlot}
                        disabled={!row.include}
                        onChange={(e) => setLoadTemplateModal((prev) => ({
                          ...prev,
                          mapping: prev.mapping.map((r, i) => i === idx ? { ...r, targetSlot: Number(e.target.value) } : r)
                        }))}
                      >
                        {Array.from({ length: mealCount }, (_, i) => i + 1).map((mn) => (
                          <option key={mn} value={mn}>{t('hrana.meal')} {mn}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
                <div className="save-template-modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setLoadTemplateModal(null)}>
                    {t('templates.cancel')}
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleApplyLoadTemplate}
                    disabled={!loadTemplateModal.mapping.some((r) => r.include)}
                  >
                    {t('templates.loadTemplate')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {isAdmin ? (
            <>
              <div className="customer-selector">
                <label><strong>{t('common.selectCustomer')}</strong></label>
                <Select
                  options={customerOptions}
                  value={selectedCustomer ? { value: selectedCustomer, label: selectedCustomer.username, id: selectedCustomer.id } : null}
                  onChange={(selected) => handleCustomerChange(selected?.value || null)}
                  placeholder={t('common.chooseCustomer')}
                  isClearable
                  className="customer-select"
                  classNamePrefix="react-select"
                  menuPortalTarget={selectMenuProps.menuPortalTarget}
                  menuPosition={selectMenuProps.menuPosition}
                  styles={selectMenuProps.styles}
                />
              </div>

              {selectedCustomer && (
                <>
                  <div className="meal-info">
                    <p>{t('common.customer')} <strong>{selectedCustomer.username}</strong></p>
                    <p>{t('hrana.mealsPerDay')} <strong>{mealCount}</strong></p>
                  </div>

                  <div className="daily-supplements-card">
                    <h3 className="daily-supplements-heading">{t('hrana.dailySupplementsTitle')}</h3>
                    <div className="table-container">
                      <table className="hrana-table daily-supplements-table">
                        <thead>
                          <tr>
                            <th>{t('hrana.supplements')}</th>
                            <th>{t('hrana.amount')}</th>
                            <th>{t('hrana.dose')}</th>
                            <th>{t('hrana.time')}</th>
                            <th className="daily-supplements-actions-col" aria-hidden="true" />
                          </tr>
                        </thead>
                        <tbody>
                          {dailySupplements.supplements.map((supVal, rowIndex) => {
                            const amountVal = dailySupplements.amounts[rowIndex] || '';
                            const timeVal = dailySupplements.times[rowIndex] || '';
                            const doseVal = (dailySupplements.doses || [])[rowIndex] || '';
                            const canRemove =
                              dailySupplements.supplements.length > 1 || !!(supVal || amountVal || timeVal);
                            return (
                              <tr key={`daily-edit-${rowIndex}`}>
                                <td className="supplement-cell">
                                  <Select
                                    options={supplementOptions}
                                    value={
                                      supVal
                                        ? supplementOptions.find((opt) => opt.value === supVal) || null
                                        : null
                                    }
                                    onChange={(option) => handleDailySupplementSelect(rowIndex, option)}
                                    placeholder={t('hrana.selectSupplement')}
                                    isClearable
                                    classNamePrefix="react-select"
                                    menuPortalTarget={selectMenuProps.menuPortalTarget}
                                    menuPosition={selectMenuProps.menuPosition}
                                    styles={selectMenuProps.styles}
                                  />
                                </td>
                                <td className="row-input-cell">
                                  <input
                                    type="number"
                                    value={amountVal}
                                    onChange={(e) => handleDailyAmountChange(rowIndex, e.target.value)}
                                    className="field-input"
                                    placeholder={supVal ? t('hrana.grams') : t('hrana.addSupplementFirst')}
                                    disabled={!supVal}
                                  />
                                </td>
                                <td className="row-input-cell">
                                  <select
                                    value={doseVal}
                                    onChange={(e) => handleDailyDoseChange(rowIndex, e.target.value)}
                                    className="field-input dose-select"
                                    disabled={!supVal}
                                  >
                                    <option value="">—</option>
                                    {DOSE_UNITS.map((u) => (
                                      <option key={u} value={u}>{u}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="row-input-cell">
                                  <input
                                    type="text"
                                    value={timeVal}
                                    onChange={(e) => handleDailyTimeChange(rowIndex, e.target.value)}
                                    className="field-input"
                                    placeholder={supVal ? t('hrana.timePlaceholder') : t('hrana.addSupplementFirst')}
                                    disabled={!supVal}
                                  />
                                </td>
                                <td className="daily-supplements-actions-col">
                                  {canRemove && (
                                    <button
                                      type="button"
                                      className="remove-row-btn"
                                      onClick={() => handleRemoveDailySupplementRow(rowIndex)}
                                      aria-label={t('hrana.removeRow')}
                                    >
                                      ×
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="daily-supp-actions">
                      <button
                        type="button"
                        className="btn-secondary daily-add-row-btn"
                        onClick={handleAddDailySupplementRow}
                      >
                        + {t('hrana.addSupplementRow')}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary daily-supp-template-btn"
                        onClick={() => setShowSuppTemplateModal(true)}
                        disabled={!dailySupplements.supplements.some(Boolean)}
                      >
                        {t('templates.saveAsTemplate')}
                      </button>
                      {supplementTemplates.length > 0 && (
                        <Select
                          options={supplementTemplates.map((tmpl) => ({ value: tmpl.id, label: tmpl.name, template: tmpl }))}
                          placeholder={t('templates.selectTemplate')}
                          onChange={handleLoadSupplementTemplate}
                          value={null}
                          isClearable={false}
                          isSearchable={false}
                          classNamePrefix="react-select"
                          className="daily-supp-template-select"
                          menuPortalTarget={selectMenuProps.menuPortalTarget}
                          menuPosition={selectMenuProps.menuPosition}
                          styles={selectMenuProps.styles}
                        />
                      )}
                    </div>
                  </div>

                  {showSuppTemplateModal && (
                    <div className="save-template-modal-overlay" onClick={() => setShowSuppTemplateModal(false)}>
                      <div className="save-template-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>{t('hrana.suppTemplateTitle')}</h3>
                        <label>{t('templates.templateName')}</label>
                        <input
                          type="text"
                          className="field-input"
                          placeholder={t('templates.templateNamePlaceholder')}
                          value={suppTemplateName}
                          onChange={(e) => setSuppTemplateName(e.target.value)}
                          autoFocus
                          onKeyDown={(e) => e.key === 'Enter' && handleSaveSupplementTemplate()}
                        />
                        <div className="save-template-modal-actions">
                          <button type="button" className="btn-secondary" onClick={() => setShowSuppTemplateModal(false)}>
                            {t('templates.cancel')}
                          </button>
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={handleSaveSupplementTemplate}
                            disabled={savingSuppTemplate || !suppTemplateName.trim()}
                          >
                            {t('templates.saveTemplate')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Saveti / Tips section */}
                  <div className="tips-section-card">
                    <div className="tips-header">
                      <h3 className="tips-title">{t('hrana.tipsTitle')}</h3>
                      <button type="button" className="btn-secondary tips-add-btn" onClick={handleAddTip}>
                        + {t('hrana.addTip')}
                      </button>
                    </div>
                    {mealTips.length === 0 ? (
                      <p className="tips-empty-hint">Klikni "+ {t('hrana.addTip')}" da dodaš savet za ovog klijenta.</p>
                    ) : (
                      <div className="tips-cards">
                        {mealTips.map((tip) => (
                          <div key={tip.id} className="tip-card" onClick={() => setExpandedTip(expandedTip === tip.id ? null : tip.id)}>
                            <div className="tip-card-header">
                              <input
                                type="text"
                                className="tip-title-input"
                                placeholder={t('hrana.tipTitlePlaceholder')}
                                value={tip.title}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => handleTipChange(tip.id, 'title', e.target.value)}
                              />
                              <button
                                type="button"
                                className="tip-delete-btn"
                                onClick={(e) => { e.stopPropagation(); handleDeleteTip(tip.id); }}
                              >
                                {t('hrana.deleteTip')}
                              </button>
                            </div>
                            {expandedTip === tip.id && (
                              <textarea
                                className="tip-body-input"
                                placeholder={t('hrana.tipBodyPlaceholder')}
                                value={tip.body}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => handleTipChange(tip.id, 'body', e.target.value)}
                                rows={4}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="customer-selector">
                    <label><strong>{t('hrana.selectMealSlot')}</strong></label>
                    <Select
                      options={mealSlotOptions}
                      value={
                        mealSlotOptions.find((o) => o.value === selectedMealNumber) ||
                        mealSlotOptions[0] ||
                        null
                      }
                      onChange={(opt) => setSelectedMealNumber(opt?.value ?? 1)}
                      placeholder={t('hrana.chooseMealSlot')}
                      isClearable={false}
                      className="customer-select"
                      classNamePrefix="react-select"
                      menuPortalTarget={selectMenuProps.menuPortalTarget}
                      menuPosition={selectMenuProps.menuPosition}
                      styles={selectMenuProps.styles}
                    />
                  </div>

                  <div className="variation-toolbar">
                    <button type="button" className="btn-secondary" onClick={handleAddMealVariation}>
                      + {t('hrana.addVariation')}
                    </button>
                  </div>

                  <div className="table-actions">
                    <button
                      className="btn-secondary"
                      type="button"
                      onClick={handleDownloadMealImage}
                      disabled={isDownloading}
                    >
                      {isDownloading ? t('hrana.preparingImage') : t('hrana.downloadImage')}
                    </button>
                  </div>
                  <div className="table-container" ref={mealTableRef}>
                    <table className="hrana-table">
                      <thead>
                        <tr>
                          <th>{t('hrana.variation')}</th>
                          <th>{t('hrana.food')}</th>
                          <th>{t('hrana.grams')}</th>
                          <th>{t('hrana.nutritionTotals')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleMealVariations.flatMap((meal) => {
                          const variationIndex = mealPlanVariations.indexOf(meal);
                          if (variationIndex === -1) return [];
                          const totals = calculateMealNutrition(meal);
                          const rowCount = Math.max(meal.foods.length, meal.foodGrams.length, 0) + 1;
                          const rowIndexes = Array.from({ length: rowCount }, (_, idx) => idx);

                          return rowIndexes.map((rowIndex) => {
                            const foodValue = meal.foods[rowIndex] || '';
                            const gramsValue = meal.foodGrams[rowIndex] || '';
                            const rowIsEmpty = !foodValue && !gramsValue;
                            const showRemove = !rowIsEmpty && rowCount > 1;

                            return (
                              <tr key={`${meal.mealNumber}-${meal.variationKey}-${rowIndex}`} className={rowIndex === 0 ? 'meal-variation-start' : undefined}>
                                {rowIndex === 0 && (
                                  <td className="meal-title-cell" rowSpan={rowCount}>
                                    {visibleMealVariations.length > 1 && (
                                      <div className="variation-title-row">
                                        <button
                                          type="button"
                                          className="remove-variation-btn"
                                          onClick={() => handleRemoveMealVariation(variationIndex)}
                                        >
                                          {t('hrana.removeVariation')}
                                        </button>
                                      </div>
                                    )}
                                    <input
                                      type="text"
                                      className="variation-label-input"
                                      value={meal.title ?? ''}
                                      onChange={(e) => handleMealTitleChange(variationIndex, e.target.value)}
                                      placeholder={`${t('hrana.meal')} ${meal.mealNumber}${meal.variationKey}`}
                                      aria-label={t('hrana.variation')}
                                    />
                                  </td>
                                )}
                                <td className="food-cell">
                                  <div className="food-row">
                                    <Select
                                      options={foodOptions}
                                      value={
                                        foodValue
                                          ? foodOptions.find((opt) => opt.value === foodValue) || null
                                          : null
                                      }
                                      onChange={(option) => handleFoodSelect(variationIndex, rowIndex, option)}
                                      placeholder={t('hrana.selectFood')}
                                      isClearable
                                      classNamePrefix="react-select"
                                      menuPortalTarget={selectMenuProps.menuPortalTarget}
                                      menuPosition={selectMenuProps.menuPosition}
                                      styles={selectMenuProps.styles}
                                    />
                                    {showRemove && (
                                      <button
                                        type="button"
                                        className="remove-row-btn"
                                        onClick={() => handleRemoveRow(variationIndex, rowIndex)}
                                        aria-label={t('hrana.removeRow')}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td className="row-input-cell">
                                  {(() => {
                                    const foodMeta = getFoodMeta(foodValue);
                                    const unit = getFoodDisplayUnit(foodMeta);
                                    const placeholderText = !foodValue
                                      ? t('hrana.addFoodFirst')
                                      : unit === 'pieces'
                                        ? t('hrana.pieces')
                                        : t('hrana.gramsUnit');
                                    return (
                                      <input
                                        type="number"
                                        value={gramsValue}
                                        onChange={(e) => handleFoodGramChange(variationIndex, rowIndex, e.target.value)}
                                        className="field-input"
                                        placeholder={placeholderText}
                                        disabled={!foodValue}
                                      />
                                    );
                                  })()}
                                </td>
                                {rowIndex === 0 && (
                                  <td className="nutrition-totals" rowSpan={rowCount}>
                                    <div className="nutrition-item">
                                      <span className="nutrition-label">{t('hrana.protein')}</span>
                                      <span className="nutrition-value">{totals.protein}g</span>
                                    </div>
                                    <div className="nutrition-item">
                                      <span className="nutrition-label">{t('hrana.carbs')}</span>
                                      <span className="nutrition-value">{totals.carbs}g</span>
                                    </div>
                                    <div className="nutrition-item">
                                      <span className="nutrition-label">{t('hrana.fats')}</span>
                                      <span className="nutrition-value">{totals.fats}g</span>
                                    </div>
                                    <div className="nutrition-item">
                                      <span className="nutrition-label">{t('hrana.calories')}</span>
                                      <span className="nutrition-value calories">{totals.calories} kcal</span>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          });
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {!selectedCustomer && (
                <div className="no-customer-selected">
                  <p>{t('common.pleaseSelectCustomer')}</p>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="meal-info">
                <p>{t('hrana.mealsPerDay')} <strong>{mealCount}</strong></p>
              </div>

              <div className="daily-supplements-card daily-supplements-readonly">
                <h3 className="daily-supplements-heading">{t('hrana.dailySupplementsTitle')}</h3>
                <div className="table-container">
                  <table className="hrana-table daily-supplements-table">
                    <thead>
                      <tr>
                        <th>{t('hrana.supplements')}</th>
                        <th>{t('hrana.amount')}</th>
                        <th>{t('hrana.dose')}</th>
                        <th>{t('hrana.time')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const dd = trimDailySupplementEdges(
                          dailySupplements.supplements,
                          dailySupplements.amounts,
                          dailySupplements.times,
                          dailySupplements.doses || []
                        );
                        const rows = dd.supplements.map((name, idx) => ({
                          name,
                          amt: dd.amounts[idx] || '',
                          dose: dd.doses[idx] || '',
                          time: dd.times[idx] || ''
                        })).filter((r) => r.name && String(r.name).trim());
                        const displayRows = rows.length > 0 ? rows : [{ name: '', amt: '', dose: '', time: '' }];
                        return displayRows.map((r, idx) => (
                          <tr key={`daily-ro-${idx}`}>
                            <td className="read-only-value">{r.name ? r.name : '—'}</td>
                            <td className="read-only-value">{r.name ? r.amt || '—' : '—'}</td>
                            <td className="read-only-value">{r.name ? r.dose || '—' : '—'}</td>
                            <td className="read-only-value">{r.name ? r.time || '—' : '—'}</td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

              {mealTips.length > 0 && (
                <div className="tips-section tips-section-readonly">
                  <div className="tips-header">
                    <h3 className="tips-title">{t('hrana.tipsTitle')}</h3>
                  </div>
                  <div className="tips-cards">
                    {mealTips.map((tip) => (
                      <div
                        key={tip.id}
                        className="tip-card tip-card-readonly"
                        onClick={() => setExpandedTip(expandedTip === tip.id ? null : tip.id)}
                      >
                        <div className="tip-card-header">
                          <span className="tip-title-readonly">{tip.title || t('hrana.tipTitlePlaceholder')}</span>
                          <span className="tip-chevron">{expandedTip === tip.id ? '▲' : '▼'}</span>
                        </div>
                        {expandedTip === tip.id && (
                          <div className="tip-body-readonly">{tip.body}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="customer-selector">
                <label><strong>{t('hrana.selectMealSlot')}</strong></label>
                <Select
                  options={mealSlotOptions}
                  value={
                    mealSlotOptions.find((o) => o.value === selectedMealNumber) ||
                    mealSlotOptions[0] ||
                    null
                  }
                  onChange={(opt) => setSelectedMealNumber(opt?.value ?? 1)}
                  placeholder={t('hrana.chooseMealSlot')}
                  isClearable={false}
                  className="customer-select"
                  classNamePrefix="react-select"
                  menuPortalTarget={selectMenuProps.menuPortalTarget}
                  menuPosition={selectMenuProps.menuPosition}
                  styles={selectMenuProps.styles}
                />
              </div>

              <div className="table-actions">
                <button
                  className="btn-secondary"
                  type="button"
                  onClick={handleDownloadMealImage}
                  disabled={isDownloading}
                >
                  {isDownloading ? t('hrana.preparingImage') : t('hrana.downloadImage')}
                </button>
              </div>
              <div className="table-container" ref={mealTableRef}>
                <table className="hrana-table">
                  <thead>
                    <tr>
                      <th>{t('hrana.variation')}</th>
                      <th>{t('hrana.food')}</th>
                      <th>{t('hrana.grams')}</th>
                      <th>{t('hrana.nutritionTotals')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleMealVariations.flatMap((meal) => {
                      const totals = calculateMealNutrition(meal);
                      const rowCount = Math.max(meal.foods.length, meal.foodGrams.length, 0);
                      if (rowCount === 0) return [];
                      const rowIndexes = Array.from({ length: rowCount }, (_, idx) => idx);

                      return rowIndexes.map((rowIndex) => {
                        const foodValue = meal.foods[rowIndex] || '';
                        const gramsValue = meal.foodGrams[rowIndex] || '';

                        return (
                          <tr key={`${meal.mealNumber}-${meal.variationKey}-readonly-${rowIndex}`} className={rowIndex === 0 ? 'meal-variation-start' : undefined}>
                            {rowIndex === 0 && (
                              <td className="meal-title-cell" rowSpan={rowCount}>
                                <span className="variation-label-readonly">
                                  {meal.title?.trim() ||
                                    `${t('hrana.meal')} ${meal.mealNumber}${meal.variationKey}`}
                                </span>
                              </td>
                            )}
                            <td className="food-cell read-only">
                              {foodValue ? (
                                <span className="food-display-item">
                                  {foodDisplayName(foodValue, getFoodMeta(foodValue)?.name_en)}
                                </span>
                              ) : null}
                            </td>
                            <td className="read-only-value">
                              {(() => {
                                const foodMeta = getFoodMeta(foodValue);
                                const unit = getFoodDisplayUnit(foodMeta);
                                const displayValue = foodValue ? gramsValue || '—' : '—';
                                const suffix =
                                  foodValue && displayValue !== '—'
                                    ? unit === 'pieces'
                                      ? ` ${t('hrana.pieces')}`
                                      : ' g'
                                    : '';
                                return (
                                  <>
                                    {displayValue}
                                    {suffix}
                                  </>
                                );
                              })()}
                            </td>
                            {rowIndex === 0 && (
                              <td className="nutrition-totals" rowSpan={rowCount}>
                                <div className="nutrition-item">
                                  <span className="nutrition-label">{t('hrana.protein')}</span>
                                  <span className="nutrition-value">{totals.protein}g</span>
                                </div>
                                <div className="nutrition-item">
                                  <span className="nutrition-label">{t('hrana.carbs')}</span>
                                  <span className="nutrition-value">{totals.carbs}g</span>
                                </div>
                                <div className="nutrition-item">
                                  <span className="nutrition-label">{t('hrana.fats')}</span>
                                  <span className="nutrition-value">{totals.fats}g</span>
                                </div>
                                <div className="nutrition-item">
                                  <span className="nutrition-label">{t('hrana.calories')}</span>
                                  <span className="nutrition-value calories">{totals.calories} kcal</span>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {mealPlanVariations.length > 0 && (!isAdmin || selectedCustomer) && (
            <div ref={mealExportRef} className="meal-plan-export-pane" aria-hidden="true">
              <div className="daily-supplements-card daily-supplements-readonly export-daily-block">
                <h3 className="daily-supplements-heading">{t('hrana.dailySupplementsTitle')}</h3>
                <div className="table-container">
                  <table className="hrana-table daily-supplements-table">
                    <thead>
                      <tr>
                        <th>{t('hrana.supplements')}</th>
                        <th>{t('hrana.amount')}</th>
                        <th>{t('hrana.dose')}</th>
                        <th>{t('hrana.time')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const dd = trimDailySupplementEdges(
                          dailySupplements.supplements,
                          dailySupplements.amounts,
                          dailySupplements.times,
                          dailySupplements.doses || []
                        );
                        const rows = dd.supplements
                          .map((name, idx) => ({ name, amt: dd.amounts[idx] || '', dose: dd.doses[idx] || '', time: dd.times[idx] || '' }))
                          .filter((r) => r.name && String(r.name).trim());
                        if (rows.length === 0) {
                          return (
                            <tr>
                              <td className="read-only-value" colSpan={4}>
                                —
                              </td>
                            </tr>
                          );
                        }
                        return rows.map((r, idx) => (
                          <tr key={`ex-daily-${idx}`}>
                            <td className="read-only-value">{r.name}</td>
                            <td className="read-only-value">{r.amt || '—'}</td>
                            <td className="read-only-value">{r.dose || '—'}</td>
                            <td className="read-only-value">{r.time || '—'}</td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="table-container">
                <table className="hrana-table">
                  <thead>
                    <tr>
                      <th>{t('hrana.variation')}</th>
                      <th>{t('hrana.food')}</th>
                      <th>{t('hrana.grams')}</th>
                      <th>{t('hrana.nutritionTotals')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mealPlanVariations.flatMap((meal) => {
                      const totals = calculateMealNutrition(meal);
                      const rowCount = Math.max(meal.foods.length, meal.foodGrams.length, 0);
                      if (rowCount === 0) return [];
                      const rowIndexes = Array.from({ length: rowCount }, (_, idx) => idx);

                      return rowIndexes.map((rowIndex) => {
                        const foodValue = meal.foods[rowIndex] || '';
                        const gramsValue = meal.foodGrams[rowIndex] || '';

                        return (
                          <tr key={`export-${meal.mealNumber}-${meal.variationKey}-${rowIndex}`} className={rowIndex === 0 ? 'meal-variation-start' : undefined}>
                            {rowIndex === 0 && (
                              <td className="meal-title-cell" rowSpan={rowCount}>
                                <span className="variation-label-readonly">
                                  {meal.title?.trim() ||
                                    `${t('hrana.meal')} ${meal.mealNumber}${meal.variationKey}`}
                                </span>
                              </td>
                            )}
                            <td className="food-cell read-only">
                              {foodValue ? (
                                <span className="food-display-item">
                                  {foodDisplayName(foodValue, getFoodMeta(foodValue)?.name_en)}
                                </span>
                              ) : null}
                            </td>
                            <td className="read-only-value">
                              {(() => {
                                const foodMeta = getFoodMeta(foodValue);
                                const unit = getFoodDisplayUnit(foodMeta);
                                const displayValue = foodValue ? gramsValue || '—' : '—';
                                const suffix =
                                  foodValue && displayValue !== '—'
                                    ? unit === 'pieces'
                                      ? ` ${t('hrana.pieces')}`
                                      : ' g'
                                    : '';
                                return (
                                  <>
                                    {displayValue}
                                    {suffix}
                                  </>
                                );
                              })()}
                            </td>
                            {rowIndex === 0 && (
                              <td className="nutrition-totals" rowSpan={rowCount}>
                                <div className="nutrition-item">
                                  <span className="nutrition-label">{t('hrana.protein')}</span>
                                  <span className="nutrition-value">{totals.protein}g</span>
                                </div>
                                <div className="nutrition-item">
                                  <span className="nutrition-label">{t('hrana.carbs')}</span>
                                  <span className="nutrition-value">{totals.carbs}g</span>
                                </div>
                                <div className="nutrition-item">
                                  <span className="nutrition-label">{t('hrana.fats')}</span>
                                  <span className="nutrition-value">{totals.fats}g</span>
                                </div>
                                <div className="nutrition-item">
                                  <span className="nutrition-label">{t('hrana.calories')}</span>
                                  <span className="nutrition-value calories">{totals.calories} kcal</span>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sticky save button for mobile */}
      {isAdmin && selectedCustomer && (
        <div className="mobile-save-bar">
          <button onClick={handleSave} className="btn-primary">
            {t('common.saveChanges')}
          </button>
          {saved && <span className="saved-indicator">{t('common.saved')}</span>}
        </div>
      )}
    </div>
  );
}

export default Hrana;